import React, { useEffect, useState } from 'react';
import { EyeOff, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import axios from '../../utils/axiosConfig';
import { useParams } from 'react-router-dom';
import EditableTitle from './EditableTitle';

const emptyService = () => ({ title: '', description: '' });
const configuredServices = (client) => (
  client?.servicesConfigured && Array.isArray(client.services) ? client.services : []
);

export default function ServicesSection({ cliente, isAdmin }) {
  const { slug } = useParams();
  const [show, setShow] = useState(Boolean(cliente?.showServices));
  const [configured, setConfigured] = useState(Boolean(cliente?.servicesConfigured));
  const [services, setServices] = useState(() => configuredServices(cliente));
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setShow(Boolean(cliente?.showServices));
    setConfigured(Boolean(cliente?.servicesConfigured));
    setServices(configuredServices(cliente));
  }, [cliente]);

  const visibleServices = services.filter((service) => service?.title?.trim() || service?.description?.trim());
  const isPublished = show && configured && visibleServices.length > 0;

  const notify = (type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 2500);
  };

  const onChange = (index, field, value) => {
    setServices((current) => current.map((service, itemIndex) => (
      itemIndex === index ? { ...service, [field]: value } : service
    )));
  };

  const startEditing = () => {
    if (!services.length) setServices([emptyService()]);
    setEdit(true);
  };

  const saveServices = async () => {
    const nextServices = services
      .map((service) => ({ title: (service.title || '').trim(), description: (service.description || '').trim() }))
      .filter((service) => service.title || service.description);

    if (!nextServices.length || nextServices.some((service) => !service.title || !service.description)) {
      notify('error', 'Completá título y descripción para cada servicio.');
      return;
    }

    try {
      setSaving(true);
      const { data } = await axios.put(`/api/clients/services/${slug}`, { services: nextServices }, { withCredentials: true });
      await axios.put(`/api/clients/visibility/${slug}`, { show: true }, { withCredentials: true });
      setServices(data.services || nextServices);
      setConfigured(true);
      setShow(true);
      setEdit(false);
      notify('success', 'Servicios publicados.');
    } catch (error) {
      notify('error', error?.response?.data?.error || 'No se pudieron guardar los servicios.');
    } finally {
      setSaving(false);
    }
  };

  const setVisibility = async (next) => {
    try {
      setSaving(true);
      const { data } = await axios.put(`/api/clients/visibility/${slug}`, { show: next }, { withCredentials: true });
      setShow(Boolean(data.show));
      notify('success', next ? 'Sección publicada.' : 'Sección oculta.');
    } catch (error) {
      notify('error', error?.response?.data?.error || 'No se pudo actualizar la visibilidad.');
    } finally {
      setSaving(false);
    }
  };

  const saveTitle = async (value) => {
    await axios.put(`/api/clients/titles/${slug}`, { servicesTitle: value }, { withCredentials: true });
  };

  if (!isPublished && !isAdmin) return null;

  return (
    <section>
      <div className="mb-8 flex flex-col justify-between gap-4 border-b border-white/10 pb-6 sm:flex-row sm:items-end">
        <div>
          <p className="mb-3 text-[11px] font-black uppercase tracking-[2px] text-primary-orange">Propuesta</p>
          <EditableTitle
            as="h2"
            className="text-3xl font-black text-white sm:text-4xl"
            value={cliente?.texts?.servicesTitle}
            placeholder="Servicios"
            disabled={!isAdmin}
            onSave={saveTitle}
          />
        </div>

        {isAdmin && isPublished && !edit ? (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setVisibility(false)}
              disabled={saving}
              className="inline-flex h-10 items-center justify-center border border-white/15 px-3 text-white/65 transition-colors hover:bg-white/5"
              title="Ocultar servicios"
            >
              <EyeOff size={17} />
            </button>
            <button
              type="button"
              onClick={startEditing}
              className="inline-flex h-10 items-center gap-2 bg-primary-orange px-4 text-sm font-bold text-white transition-colors hover:bg-orange-500"
            >
              <Pencil size={16} />
              Editar
            </button>
          </div>
        ) : null}
      </div>

      {!isPublished && isAdmin && !edit ? (
        <div className="flex flex-col justify-between gap-6 border border-dashed border-white/20 p-6 sm:flex-row sm:items-center">
          <div>
            <h3 className="text-lg font-bold text-white">Mostrá solo los servicios que realmente ofrecés</h3>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/50">Esta sección está oculta hasta que cargues y publiques tus propios servicios.</p>
          </div>
          <button
            type="button"
            onClick={startEditing}
            className="inline-flex h-11 shrink-0 items-center justify-center gap-2 bg-primary-orange px-4 text-sm font-bold text-white transition-colors hover:bg-orange-500"
          >
            <Plus size={18} />
            Cargar servicios
          </button>
        </div>
      ) : null}

      {edit ? (
        <div className="space-y-4">
          {services.map((service, index) => (
            <div key={index} className="grid gap-4 border border-white/10 bg-white/[0.02] p-5 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)_auto] md:items-start">
              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-[1.5px] text-white/40">Servicio</span>
                <input
                  value={service.title || ''}
                  onChange={(event) => onChange(index, 'title', event.target.value)}
                  maxLength={80}
                  className="mt-2 h-11 w-full border border-white/10 bg-black/20 px-3 text-sm font-semibold text-white outline-none transition-colors focus:border-primary-orange"
                  placeholder="Ej. Catering"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-[1.5px] text-white/40">Descripción</span>
                <textarea
                  value={service.description || ''}
                  onChange={(event) => onChange(index, 'description', event.target.value)}
                  maxLength={240}
                  rows={2}
                  className="mt-2 w-full resize-none border border-white/10 bg-black/20 px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-primary-orange"
                  placeholder="Contá qué incluye"
                />
              </label>
              <button
                type="button"
                onClick={() => setServices((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                className="mt-6 inline-flex h-11 w-11 items-center justify-center border border-red-400/25 text-red-300 transition-colors hover:bg-red-500/10"
                title="Quitar servicio"
              >
                <Trash2 size={17} />
              </button>
            </div>
          ))}

          <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
            <button
              type="button"
              onClick={() => setServices((current) => current.length >= 6 ? current : [...current, emptyService()])}
              disabled={services.length >= 6}
              className="inline-flex h-10 items-center gap-2 border border-white/15 px-3 text-sm font-bold text-white/75 transition-colors hover:bg-white/5 disabled:opacity-40"
            >
              <Plus size={17} />
              Agregar servicio
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setEdit(false); setServices(configuredServices(cliente)); }}
                className="inline-flex h-10 items-center gap-2 border border-white/15 px-3 text-sm font-bold text-white/65 transition-colors hover:bg-white/5"
              >
                <X size={17} />
                Cancelar
              </button>
              <button
                type="button"
                onClick={saveServices}
                disabled={saving}
                className="inline-flex h-10 items-center gap-2 bg-primary-orange px-4 text-sm font-bold text-white transition-colors hover:bg-orange-500 disabled:opacity-50"
              >
                <Save size={17} />
                {saving ? 'Guardando' : 'Publicar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isPublished && !edit ? (
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
          {visibleServices.map((service, index) => (
            <article key={`${service.title}-${index}`} className="bg-[#111111] p-6 sm:p-7">
              <p className="text-[11px] font-black uppercase tracking-[2px] text-primary-orange">{String(index + 1).padStart(2, '0')}</p>
              <h3 className="mt-7 text-xl font-black text-white">{service.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/55">{service.description}</p>
            </article>
          ))}
        </div>
      ) : null}

      {toast ? <p className={`mt-4 text-sm font-semibold ${toast.type === 'success' ? 'text-emerald-400' : 'text-red-300'}`}>{toast.message}</p> : null}
    </section>
  );
}
